import * as React from 'react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

const PhoneInput = React.forwardRef(({ className, onChange, ...props }, ref) => {
  return (
    <RPNInput.default
      ref={ref}
      className={`flex ${className || ''}`}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      onChange={(value) => onChange?.(value || '')}
      {...props}
    />
  );
});
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef(({ className, ...props }, ref) => (
  <input
    className={`rounded-e-lg rounded-s-none px-2 bg-background outline-none ${className || ''}`}
    {...props}
    ref={ref}
  />
));
InputComponent.displayName = 'InputComponent';

const CountrySelect = ({ disabled, value, onChange, options }) => {
  const [isDropdownVisible, setDropdownVisible] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const searchInputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  // Click-away listener
  React.useEffect(() => {
    if (!isDropdownVisible) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownVisible]);

  // Auto-focus search input when dropdown opens
  React.useEffect(() => {
    if (isDropdownVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownVisible]);

  const toggleDropdown = () => {
    if (!disabled) {
      setDropdownVisible((prev) => !prev);
    }
  };

  const handleSelect = React.useCallback(
    (country) => {
      onChange(country);
      setDropdownVisible(false);
    },
    [onChange]
  );

  const filteredOptions = options.filter(
    (option) =>
      option.value &&
      (option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        RPNInput.getCountryCallingCode(option.value).includes(searchTerm))
  );

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        className={`flex items-center gap-1 px-3 py-2 border rounded-lg ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={disabled}
        onMouseDown={(e) => { e.preventDefault(); toggleDropdown(); }}
      >
        <FlagComponent country={value} countryName={value} />
        <span className="ml-2">▼</span>
      </button>
      {isDropdownVisible && (
        <div className="absolute z-10 mt-1 w-64 bg-white border rounded-lg shadow-lg">
          <input
            type="text"
            placeholder="Search country..."
            className="w-full p-2 border-b outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            ref={searchInputRef}
          />
          <div className="max-h-72 overflow-y-auto">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                  option.value === value ? 'font-bold' : ''
                }`}
                onClick={() => handleSelect(option.value)}
              >
                <FlagComponent country={option.value} countryName={option.label} />
                <span className="ml-2">{option.label}</span>
                <span className="text-gray-500">+{RPNInput.getCountryCallingCode(option.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FlagComponent = ({ country, countryName }) => {
  const Flag = flags[country];
  return (
    <span className="bg-gray-200 flex h-4 w-6 overflow-hidden rounded-sm">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};
FlagComponent.displayName = 'FlagComponent';

export { PhoneInput };
